import { useMemo, useState } from 'react';
import { toCommaListIfNumbers, commaListCount } from '@/utils/search-list';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  PhoneCall, Loader2, Search, Download, History, PhoneOutgoing, Filter,
  BarChart3, X, FileAudio, RotateCw,
} from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { CallRecordingPlayer } from '@/components/order/CallRecordingPlayer';
import { Modal } from '@/components/ui/Modal';
import { CopyText } from '@/components/ui/CopyText';
import { GmailPagination } from '@/components/ui/GmailPagination';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { CallRemarksDialog } from '@/components/order/CallRemarksDialog';
import { CustomerRemarkSelect } from './CustomerRemarkSelect';
import { refreshCallLists } from './refreshCallLists';

type CallRow = {
  id: string;
  direction: 'OUTBOUND' | 'INBOUND';
  status: string;
  reason?: string | null;
  merchantId?: number | null;
  merchantName?: string | null;
  orderId?: string | null;
  orderNo?: string | null;
  awbNo?: string | null;
  lrNo?: string | null;
  orderType?: 'B2C' | 'B2B' | null;
  customerName?: string | null;
  fromNumber: string;
  toNumber: string;
  durationSec?: number | null;
  startedAt?: string | null;
  startedAtIsExact?: boolean;
  completedAt?: string | null;
  createdAt: string;
  recordingUrl?: string | null;
  providerCallId?: string | null;
  ref?: string | null;
  matchedVia?: string | null;
  actorUserName?: string | null;
  remarks?: string | null;
  customerRemarks?: string | null;
};
type CallDetail = CallRow & { responseStatus?: number | null; responseBody?: string | null; rawPayload?: any; history: CallRow[] };

const STATUSES = ['INITIATED', 'COMPLETED', 'CONNECTED', 'NO_ANSWER', 'BUSY', 'FAILED'];

/** Calendar date N days back in IST — matches how the server bounds a day (+05:30). */
function istDay(daysAgo: number) {
  const d = new Date(Date.now() + 5.5 * 3600 * 1000);
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}
const dt = (v?: string | null) => (v ? new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
/** Talk time as m:ss. A call with no reported duration shows a dash, never a misleading 0s. */
const dur = (s?: number | null) => (s == null ? '—' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);

const STATUS_STYLE: Record<string, string> = {
  COMPLETED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  CONNECTED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  INITIATED: 'bg-amber-100 text-amber-800 border-amber-200',
  NO_ANSWER: 'bg-slate-100 text-slate-600 border-slate-200',
  BUSY: 'bg-slate-100 text-slate-600 border-slate-200',
  FAILED: 'bg-rose-100 text-rose-700 border-rose-200',
};

export function IvrPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [direction, setDirection] = useState('');
  const [merchantId, setMerchantId] = useState<number | ''>('');
  const [from, setFrom] = useState(istDay(29));
  const [to, setTo] = useState(istDay(0));
  const [withRecordingOnly, setWithRecordingOnly] = useState(false);
  // Calls remarked "customer will accept delivery" need nothing more and are hidden unless asked for.
  const [showAccepted, setShowAccepted] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [detailId, setDetailId] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showUserSummary, setShowUserSummary] = useState(false);
  const [redialing, setRedialing] = useState<string | null>(null);

  const params = useMemo(() => {
    const p: any = { page, pageSize };
    if (search.trim()) p.search = search.trim();
    if (status) p.status = status;
    if (direction) p.direction = direction;
    if (merchantId) p.merchantId = merchantId;
    if (from) p.from = from;
    if (to) p.to = to;
    if (withRecordingOnly) p.withRecordingOnly = true;
    if (showAccepted) p.showAccepted = 1;
    return p;
  }, [page, search, status, direction, merchantId, from, to, withRecordingOnly, showAccepted]);

  const { data, isLoading } = useQuery({
    queryKey: ['ivr-logs', params],
    queryFn: async () => (await api.get<{ total: number; items: CallRow[] }>('/ivr/logs', { params })).data,
    // Calls change after they are placed (connected, completed, recording arrives from the vendor),
    // so the list keeps itself current while it is on screen instead of waiting for a reload.
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const { data: merchants } = useQuery({
    queryKey: ['merchants-light-ivr'],
    queryFn: async () => (await api.get<any>('/merchants', { params: { pageSize: 500 } })).data,
  });
  const merchantList = useMemo(() => (Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? []), [merchants]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  function resetFilters() {
    setSearch(''); setStatus(''); setDirection(''); setMerchantId('');
    setFrom(istDay(29)); setTo(istDay(0)); setWithRecordingOnly(false); setShowAccepted(false); setPage(1);
  }

  async function exportCsv() {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '' && k !== 'page' && k !== 'pageSize') qs.set(k, String(v)); });
    await downloadBlob(`/ivr/logs/export?${qs.toString()}`, `ivr-call-logs-${istDay(0)}.csv`);
  }

  // Re-dial asks what the call is about first, exactly as the order screens do.
  const [redialAsk, setRedialAsk] = useState<CallRow | null>(null);

  /** Re-dial the same customer, carrying the shipment reference so the new call links to it too. */
  async function redial(r: CallRow, remarks?: string) {
    setRedialing(r.id);
    try {
      const res: any = (await api.post('/ivr/call', {
        toNumber: r.toNumber,
        orderId: r.orderId ?? undefined,
        awbNo: r.awbNo ?? undefined,
        lrNo: r.lrNo ?? undefined,
        reason: `Re-dial of call #${r.id}`,
        remarks: remarks || undefined,
      })).data;
      if (res?.ok) toast.success(`Calling ${r.toNumber} from ${res.agentNumber}${res.usedUserIvrMobile ? ' (your IVR mobile)' : ''}`);
      else toast.error(`Provider refused: ${res?.providerOutcome ?? res?.providerResponse ?? 'unknown error'}`);
      refreshCallLists(qc);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not place the call');
    } finally {
      setRedialing(null);
    }
  }

  return (
    <div className="p-6 max-w-[1700px] mx-auto">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <PhoneCall size={22} className="text-primary" /> IVR
          </h1>
          <p className="text-sm text-slate-500">Every call placed or reported by the IVR provider — with the shipment and merchant behind it.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setShowSummary(true)}
            className="px-4 py-2 border border-primary text-primary rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/5">
            <BarChart3 size={14} /> Merchant Summary
          </button>
          {/* The merchant view answers "which client is being called?"; this one answers "who is
              doing the calling, and is their work landing?" — the question asked when follow-up
              on delivered orders is running behind. */}
          <button onClick={() => setShowUserSummary(true)}
            className="px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-emerald-50">
            <BarChart3 size={14} /> User Summary
          </button>
          <button onClick={exportCsv}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <label className="text-xs text-slate-500">Search</label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search}
                // Paste a column of AWBs or call references and the separators become commas as you
                // go, the same as every other search box on the platform. The API matches ANY of them.
                onChange={(e) => { setSearch(toCommaListIfNumbers(e.target.value)); setPage(1); }}
                placeholder="Mobile / AWB / LR / call id — paste several, separated by commas"
                className="input h-9 pl-8 w-full text-sm" />
            </div>
            {commaListCount(search) > 1 && (
              <div className="text-[11px] text-indigo-600 mt-0.5">
                Searching {commaListCount(search)} references
              </div>
            )}
          </div>
          <div className="md:col-span-3">
            <label className="text-xs text-slate-500">Merchant</label>
            <SearchableSelect
              value={merchantId === '' ? null : merchantId}
              onChange={(v: any) => { setMerchantId(v == null ? '' : Number(v)); setPage(1); }}
              options={merchantList.map((m: any) => ({ value: m.id, label: `${m.companyName} (#${m.id})` }))}
              placeholder="All merchants" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500">Status</label>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="input h-9 w-full text-sm">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500">Direction</label>
            <select value={direction} onChange={(e) => { setDirection(e.target.value); setPage(1); }} className="input h-9 w-full text-sm">
              <option value="">All</option>
              <option value="OUTBOUND">Click-to-call</option>
              <option value="INBOUND">Vendor-reported</option>
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="text-xs text-slate-500">From</label>
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="input h-9 w-full text-sm" />
          </div>
          <div className="md:col-span-1">
            <label className="text-xs text-slate-500">To</label>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="input h-9 w-full text-sm" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-slate-100">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={withRecordingOnly} onChange={(e) => { setWithRecordingOnly(e.target.checked); setPage(1); }} />
            With recording only
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" title="Answered calls remarked 'Customer will accept delivery' are hidden from this list — they need no follow-up. Unanswered calls stay, and searching an AWB / LR / number always finds its calls.">
            <input type="checkbox" checked={showAccepted} onChange={(e) => { setShowAccepted(e.target.checked); setPage(1); }} data-testid="ivr-show-accepted" />
            Show "accepted delivery" calls
          </label>
          <button onClick={resetFilters} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
            <X size={11} /> Clear filters
          </button>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-500"><Filter size={11} className="inline mr-1" />{total.toLocaleString('en-IN')} calls</span>
            <GmailPagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Merchant</th>
                <th className="text-left px-3 py-2.5">AWB / LR</th>
                <th className="text-left px-3 py-2.5">Order No</th>
                <th className="text-left px-3 py-2.5">Customer</th>
                <th className="text-left px-3 py-2.5">Type</th>
                <th className="text-left px-3 py-2.5">From</th>
                <th className="text-left px-3 py-2.5">To</th>
                <th className="text-right px-3 py-2.5">Duration</th>
                <th className="text-left px-3 py-2.5">Start</th>
                <th className="text-left px-3 py-2.5">End</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-left px-3 py-2.5">Customer Remarks</th>
                <th className="text-left px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={13} className="text-center py-10"><Loader2 size={20} className="mx-auto animate-spin text-primary" /></td></tr>}
              {!isLoading && items.length === 0 && <tr><td colSpan={13} className="text-center py-12 text-slate-400">No calls match these filters.</td></tr>}
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-xs">
                    <div className="text-slate-900">{r.merchantName ?? <span className="text-slate-400">Not linked</span>}</div>
                    {r.actorUserName && <div className="text-slate-400">by {r.actorUserName}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.awbNo || r.lrNo
                      ? <CopyText value={r.awbNo || r.lrNo || ''} display={<span className="font-mono">{r.awbNo || r.lrNo}</span>} iconSize={10} silent />
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">{r.orderNo ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{r.customerName ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.orderType ?? <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600">{r.fromNumber || '—'}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-900">{r.toNumber}</td>
                  <td className="px-3 py-2 text-xs text-right tabular-nums">{dur(r.durationSec)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {dt(r.startedAt)}
                    {/* A start time we inferred from when WE placed the call is not the vendor's own. */}
                    {!r.startedAtIsExact && <span className="text-slate-400" title="Provider never reported a start time — showing when the call was placed"> *</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.completedAt ? dt(r.completedAt) : <span className="text-amber-600" title="The provider has not reported this call's outcome">Pending</span>}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className={`px-2 py-0.5 rounded border ${STATUS_STYLE[r.status] ?? 'bg-slate-100 text-slate-600 border-slate-200'}`}>{r.status}</span>
                  </td>
                  {/* The outcome, chosen after the call. The free-text note typed before dialling is
                      shown under it, because the two answer different questions. */}
                  <td className="px-3 py-2">
                    <CustomerRemarkSelect callId={r.id} value={r.customerRemarks} />
                    {r.remarks && (
                      <div className="text-[10px] text-slate-400 mt-0.5 max-w-[13rem] truncate" title={r.remarks}>{r.remarks}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setDetailId(r.id)} title="View history"
                        className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1"><History size={11} /></button>
                      {/* Single-call recording download. Was an unlabelled, permanently-greyed icon —
                          indistinguishable from "not implemented". Now it is an explicit download link
                          when a recording exists, and says why when it doesn't. */}
                      {r.recordingUrl
                        ? <CallRecordingPlayer callId={r.id} compact />
                        : <span title="The IVR provider has not pushed a recording for this call — see Settings → IVR for the webhook URL to give them"
                            className="text-xs px-2 py-1 border border-dashed border-slate-200 rounded text-slate-400 flex items-center gap-1">
                            <FileAudio size={11} /> No rec.
                          </span>}
                      <button onClick={() => setRedialAsk(r)} disabled={redialing === r.id} title="Re-dial this number"
                        className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1">
                        {redialing === r.id ? <Loader2 size={11} className="animate-spin" /> : <PhoneOutgoing size={11} />} Re-dial
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Recordings only ever exist if the provider pushes them to our webhook. When none of the calls
            on screen has one, say so once here instead of leaving a row of dead buttons unexplained. */}
        {!isLoading && items.length > 0 && items.every((r) => !r.recordingUrl) && (
          <div className="px-3 py-2 border-t border-slate-100 text-xs text-amber-700 bg-amber-50">
            No recordings on these calls. We store a recording as soon as the IVR provider pushes it to our
            webhook — the exact URL and field names to hand them are in <b>Settings → IVR</b>.
          </div>
        )}
        <div className="flex items-center justify-end px-3 py-2 border-t border-slate-100">
          <GmailPagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
        </div>
      </div>

      {detailId && <CallHistoryModal id={detailId} onClose={() => setDetailId(null)} />}
      {showSummary && <IvrSummaryModal onClose={() => setShowSummary(false)} />}
      {showUserSummary && <IvrUserSummaryModal onClose={() => setShowUserSummary(false)} />}
      <CallRemarksDialog open={!!redialAsk} toNumber={redialAsk?.toNumber ?? ''} awbNo={redialAsk?.awbNo ?? redialAsk?.lrNo}
        busy={redialing === redialAsk?.id}
        onCancel={() => setRedialAsk(null)}
        onConfirm={(remarks) => { const r = redialAsk; setRedialAsk(null); if (r) redial(r, remarks); }} />
    </div>
  );
}

function CallHistoryModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['ivr-log', id],
    queryFn: async () => (await api.get<CallDetail>(`/ivr/logs/${id}`)).data,
  });
  return (
    <Modal isOpen onClose={onClose} title="Call History"
      subtitle={data ? `${data.toNumber}${data.customerName ? ` · ${data.customerName}` : ''}` : undefined} maxWidth="3xl">
      {isLoading && <div className="py-10 text-center"><Loader2 size={20} className="mx-auto animate-spin text-primary" /></div>}
      {data && (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <KV k="Merchant" v={data.merchantName ?? 'Not linked'} />
            <KV k="Shipment" v={data.awbNo || data.lrNo || '—'} />
            <KV k="Order No" v={data.orderNo ?? '—'} />
            <KV k="Order Type" v={data.orderType ?? '—'} />
            <KV k="From / agent" v={data.fromNumber || '—'} />
            <KV k="To / customer" v={data.toNumber} />
            <KV k="Called by" v={data.actorUserName ?? '—'} />
            <KV k="Reason" v={data.reason ?? '—'} />
            <KV k="Start" v={dt(data.startedAt)} />
            <KV k="End" v={data.completedAt ? dt(data.completedAt) : 'Not reported by the provider'} />
            <KV k="Duration" v={dur(data.durationSec)} />
            <KV k="Status" v={data.status} />
            <KV k="Provider call id" v={data.providerCallId ?? '—'} />
            <KV k="Our reference" v={data.ref ?? '—'} />
          </div>

          {data.recordingUrl
            ? <div className="flex flex-col items-start gap-1">
                <CallRecordingPlayer callId={data.id} />
                <a href={data.recordingUrl} target="_blank" rel="noreferrer" className="text-[11px] text-slate-400 hover:underline">
                  Open at the provider
                </a>
              </div>
            : <div className="text-xs text-amber-600">No recording — the provider has not pushed one for this call.</div>}

          {data.responseBody && (
            <div>
              <div className="text-xs font-medium uppercase text-slate-500 mb-1">Provider response when the call was placed</div>
              <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap">{data.responseBody}</pre>
            </div>
          )}
          {data.rawPayload && (
            <div>
              <div className="text-xs font-medium uppercase text-slate-500 mb-1">Vendor webhook payload</div>
              <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(data.rawPayload, null, 2)}</pre>
            </div>
          )}

          <div>
            <div className="text-xs font-medium uppercase text-slate-500 mb-1">Other calls to {data.toNumber}</div>
            {data.history.length === 0
              ? <div className="text-xs text-slate-400">This is the only call to this number.</div>
              : (
                <table className="w-full text-xs">
                  <thead className="text-slate-500"><tr>
                    <th className="text-left py-1">When</th><th className="text-left">From</th>
                    <th className="text-right">Duration</th><th className="text-left pl-3">Status</th><th className="text-left">Recording</th>
                  </tr></thead>
                  <tbody>
                    {data.history.map((h) => (
                      <tr key={h.id} className="border-t border-slate-100">
                        <td className="py-1">{dt(h.startedAt)}</td>
                        <td className="font-mono">{h.fromNumber || '—'}</td>
                        <td className="text-right tabular-nums">{dur(h.durationSec)}</td>
                        <td className="pl-3">{h.status}</td>
                        <td>{h.recordingUrl ? <CallRecordingPlayer callId={h.id} compact /> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * IVR SUMMARY BY USER — the same period cut by the staff member who placed the call.
 *
 * Calls with no actor (inbound calls and vendor pushes) appear as a single "Unattributed" row
 * rather than being dropped: hiding them would make these totals disagree with the call log's own
 * count and send someone hunting for the missing calls.
 */
function IvrUserSummaryModal({ onClose }: { onClose: () => void }) {
  const [from, setFrom] = useState(istDay(29));
  const [to, setTo] = useState(istDay(0));
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['ivr-user-summary', from, to],
    queryFn: async () => (await api.get<any>('/ivr/summary/users', { params: { from, to } })).data,
  });
  const rows: any[] = data?.users ?? [];
  const t = data?.totals;
  const mmss = (sec: number) => {
    const s = Math.max(0, Number(sec) || 0);
    return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  };

  return (
    <Modal isOpen onClose={onClose} title="IVR Summary — by User" subtitle="Who placed the calls, and whether the work landed" maxWidth="6xl">
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input" />
          </div>
          <button onClick={() => refetch()} disabled={isFetching}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50">
            {isFetching ? 'Loading…' : 'Apply'}
          </button>
          {t && (
            <div className="ml-auto text-xs text-slate-500">
              <b>{t.users}</b> user(s) · <b>{t.callsPlaced}</b> calls · <b>{t.ordersCalled}</b> orders worked · <b>{mmss(t.talkTimeSec)}</b> talk time
            </div>
          )}
        </div>

        <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-right px-3 py-2">Calls Placed</th>
                <th className="text-right px-3 py-2">Orders Worked</th>
                <th className="text-right px-3 py-2">Merchants</th>
                <th className="text-right px-3 py-2">Call Status Pending</th>
                <th className="text-right px-3 py-2">Recordings</th>
                <th className="text-right px-3 py-2">Talk Time</th>
                <th className="text-right px-3 py-2">Avg / Call</th>
                <th className="text-left px-3 py-2">Last Call</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400">No calls in this period.</td></tr>
              ) : rows.map((r) => (
                <tr key={String(r.userId ?? 'none')} className={`border-t border-slate-100 ${r.userId == null ? 'bg-slate-50/60' : ''}`}>
                  <td className="px-3 py-2">
                    <div className={r.userId == null ? 'text-slate-500 italic' : 'text-slate-800 font-medium'}>{r.userName}</div>
                    <div className="text-[11px] text-slate-400">
                      {[r.empCode, r.userType].filter(Boolean).join(' · ')}
                      {r.inbound > 0 && <span className="ml-1">· {r.inbound} inbound</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{r.callsPlaced}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.ordersCalled}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.merchantsTouched}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${r.callStatusPending > 0 ? 'text-amber-700' : ''}`}>{r.callStatusPending}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.recordings}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{mmss(r.talkTimeSec)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{mmss(r.avgTalkSec)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.lastCallAt ? new Date(r.lastCallAt).toLocaleString('en-IN') : '—'}</td>
                </tr>
              ))}
            </tbody>
            {t && rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 bg-slate-50 font-semibold">
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.callsPlaced}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.ordersCalled}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.callStatusPending}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.recordings}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{mmss(t.talkTimeSec)}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </Modal>
  );
}

/**
 * IVR SUMMARY — only merchants who have the IVR service switched on, the same way the warehouse
 * service lists only WMS-enabled merchants.
 */
function IvrSummaryModal({ onClose }: { onClose: () => void }) {
  const [from, setFrom] = useState(istDay(29));
  const [to, setTo] = useState(istDay(0));
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['ivr-summary', from, to],
    queryFn: async () => (await api.get<any>('/ivr/summary', { params: { from, to } })).data,
  });
  const rows: any[] = data?.merchants ?? [];
  const t = data?.totals;

  return (
    <Modal isOpen onClose={onClose} title="IVR Summary" subtitle="Merchants with the IVR service enabled" maxWidth="6xl">
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input h-9 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input h-9 text-sm" />
          </div>
          <button onClick={() => refetch()} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
            {isFetching ? <Loader2 size={13} className="animate-spin" /> : <RotateCw size={13} />} Refresh
          </button>
          {t && (
            <div className="ml-auto flex flex-wrap gap-4 text-xs">
              <Tot label="Merchants" v={t.merchants} />
              <Tot label="Delivered Orders" v={t.deliveredOrders} />
              <Tot label="Call Pending" v={t.callPending} tone="text-rose-700" />
              <Tot label="Call Status Pending" v={t.callStatusPending} tone="text-amber-700" />
            </div>
          )}
        </div>

        {isLoading && <div className="py-10 text-center"><Loader2 size={20} className="mx-auto animate-spin text-primary" /></div>}
        {!isLoading && rows.length === 0 && (
          <div className="py-10 text-center text-slate-500 text-sm">
            No merchant has the IVR service enabled yet. Turn it on per merchant under <b>Merchant → Profile</b>.
          </div>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Merchant</th>
                  <th className="text-right px-3 py-2">Total Delivered Orders</th>
                  <th className="text-right px-3 py-2">Orders Called</th>
                  <th className="text-right px-3 py-2">Call Pending</th>
                  <th className="text-right px-3 py-2">Calls Placed</th>
                  <th className="text-right px-3 py-2">Call Status Pending</th>
                  <th className="text-right px-3 py-2">Recordings</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.merchantId} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="text-slate-900">{r.merchantName}</div>
                      <div className="text-xs text-slate-400">{r.brandName ? `${r.brandName} · ` : ''}#{r.merchantId}</div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.deliveredOrders.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{r.ordersCalled.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-rose-700">{r.callPending.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.callsPlaced.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">{r.callStatusPending.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.recordings.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-slate-400">
          <b>Call Pending</b> = delivered orders in this period with no call against them yet.
          <b className="ml-2">Call Status Pending</b> = calls we placed whose outcome the provider has never reported back,
          so we don't know whether they connected.
        </p>
      </div>
    </Modal>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <div className="text-xs text-slate-500 w-36 shrink-0">{k}</div>
      <div className="text-slate-900 text-sm break-all">{v}</div>
    </div>
  );
}
function Tot({ label, v, tone }: { label: string; v: number; tone?: string }) {
  return (
    <div>
      <div className="text-slate-400 uppercase text-[10px]">{label}</div>
      <div className={`text-base font-semibold ${tone ?? 'text-slate-900'}`}>{Number(v ?? 0).toLocaleString('en-IN')}</div>
    </div>
  );
}
